import { Code, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { Select } from 'src/design-system/Select';
import { MetadataRow } from './MetadataRow';
import { CODE_BLOCK_STYLE } from './styles';
import { DetectedEntity } from './types';

function ClassPanel({ entityType, entities }: { entityType: string; entities: DetectedEntity[] }) {
  const firstEntity = entities[0];
  const label = firstEntity.entityLabel || entityType || 'Unknown';
  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Title order={3}>{label}</Title>
        <Text size="sm" c="dimmed">
          {entities.length} {entities.length === 1 ? 'entity' : 'entities'} extracted
        </Text>
      </Stack>

      <Stack gap="xs">
        <MetadataRow label="Entity type" value={entityType} />
        <MetadataRow label="Model" value={firstEntity.llmModelName} />
        <MetadataRow label="Finish reason" value={firstEntity.llmFinishReason} />
      </Stack>

      <Stack gap="xs">
        <Title order={5}>Detected {label.toLowerCase()} entities</Title>
        {entities.map((detected, index) => (
          <Stack key={detected.id} gap={4}>
            <Text size="xs" c="dimmed" fw={600}>
              {label} {index + 1} of {entities.length}
            </Text>
            <Code block style={CODE_BLOCK_STYLE}>
              {JSON.stringify(detected.entity.fields, null, 2)}
            </Code>
          </Stack>
        ))}
      </Stack>

      <Stack gap="xs">
        <Title order={5}>Reasoning for {label.toLowerCase()}</Title>
        {firstEntity.llmReasoningContent ? (
          <Code block style={CODE_BLOCK_STYLE}>
            {firstEntity.llmReasoningContent}
          </Code>
        ) : (
          <Text c="dimmed" size="sm">
            No reasoning captured (non-thinking model, or detection ran before reasoning capture was
            enabled)
          </Text>
        )}
      </Stack>
    </Stack>
  );
}

function groupByEntityType(entities: DetectedEntity[]): Map<string, DetectedEntity[]> {
  const groups = new Map<string, DetectedEntity[]>();
  for (const detected of entities) {
    if (!detected) continue;
    const key = detected.entityType ?? 'Unknown';
    const existing = groups.get(key);
    if (existing) {
      existing.push(detected);
    } else {
      groups.set(key, [detected]);
    }
  }
  return groups;
}

export function EntityDetectionPanel({ entities }: { entities: DetectedEntity[] }) {
  const safeEntities = entities.filter(Boolean);
  const groups = Array.from(groupByEntityType(safeEntities).entries()).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const [selected, setSelected] = useState<string | null>(groups[0]?.[0] ?? null);

  if (groups.length === 0) {
    return <Text c="dimmed">No entities detected.</Text>;
  }

  const [activeType, activeGroup] =
    groups.find(([entityType]) => entityType === selected) ?? groups[0];

  return (
    <Stack gap="md">
      <Select
        label="Entity type"
        data={groups.map(([entityType, group]) => ({
          value: entityType,
          label: `${group[0].entityLabel || entityType} (${group.length})`,
        }))}
        value={activeType}
        onChange={value => {
          if (value) setSelected(value);
        }}
        allowDeselect={false}
        searchable
        maxDropdownHeight={320}
        style={{ maxWidth: 360 }}
      />
      <ClassPanel entityType={activeType} entities={activeGroup} />
    </Stack>
  );
}
